import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  MouseSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { PurchaseOrderItem, PurchaseOrderItemStatus } from '@domain/purchasing';
import { columns } from '../../../constants/constants';
import { GroupingOption } from '../../../types/types';
import KanbanColumn from './KanbanColumn';
import {
  handleDragOver,
  handleDragEnd,
  KanbanDragResult,
  getItemsByStatus,
  getPlaceholderInfo,
} from '../../../utils/kanbanUtils';


interface PurchaseOrdersKanbanProps {
  items?: PurchaseOrderItem[];
  group: GroupingOption;
  onStatusChange?: (item: PurchaseOrderItem, newStatus: any) => void;
}

const PurchaseOrdersKanban: React.FC<PurchaseOrdersKanbanProps> = ({
  items,
  group,
  onStatusChange
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localItems, setLocalItems] = useState<PurchaseOrderItem[]>([]);
  const [placeholder, setPlaceholder] = useState<{ status: PurchaseOrderItemStatus; insertIndex: number } | null>(null);

  useEffect(() => {
    setLocalItems(items || []);
  }, [items]);


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,

      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOverEvent = (event: DragOverEvent) => {
    if (!items) return;
    
    // Get placeholder information
    const placeholderInfo = getPlaceholderInfo(event, localItems, columns);
    setPlaceholder(placeholderInfo);
    
    // Handle reordering within the same column
    const newItems = handleDragOver(event, localItems, columns);
    setLocalItems(newItems);
  };

  const handleDragEndEvent = async (event: DragEndEvent) => {
    setActiveId(null);
    setPlaceholder(null); // Clear placeholder on drag end

    if (!items) return;
    const result: KanbanDragResult = handleDragEnd(event, localItems, columns);

    // Update local items for optimistic update
    setLocalItems(result.newItems);

    if (result.shouldCallStatusChange && result.statusChangeData && onStatusChange) {
      onStatusChange(result.statusChangeData.item, result.statusChangeData.newStatus);
    }
  };

  const activeItem = activeId ? (() => {
    // Always look in the original items array for the active item
    // This ensures the drag overlay works even when dragging to empty columns
    const item = items?.find(i => i.id && i.id === activeId);
    return item ? { item } : null;
  })() : null;

  return (
    <Box sx={{ 
      width: '100%', 
      overflow: 'auto', 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      userSelect: 'none',
      '& *': {
        userSelect: 'none'
      }
    }}>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEndEvent}
        onDragOver={handleDragOverEvent}
      >
        <Stack direction="row" gap={2} flex={1}>
          {columns.map((column) => {
            const columnItems = getItemsByStatus(localItems || [], column.id);

            return (
              <KanbanColumn
                key={column.id}
                column={column}
                group={group}
                items={columnItems.map(({ item }) => item) || []}
                onStatusChange={onStatusChange}
                activeId={activeId}
              />
            );
          })}
        </Stack>

        <DragOverlay>
          {activeItem ? (
            <Card sx={{ width: 300, opacity: 0.8 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {activeItem.item.product.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Qty: {activeItem.item.quantity}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
};

export default PurchaseOrdersKanban;